import type { ISyncStatusSummaryResponse, SyncStatusLevel } from '@skystone/shared';
import { cn } from '@/lib/utils';
import { formatRelativeTime } from '@/lib/format-relative-time';

type BadgeConfig = { dot: string; text: string; textColor: string };

const SYNC_STATUS_CONFIG: Record<Exclude<SyncStatusLevel, 'unknown'>, BadgeConfig> = {
  ok: { dot: 'bg-success-500', text: 'Sync OK', textColor: 'text-success-600' },
  partial: { dot: 'bg-warning-500', text: 'Sync delayed', textColor: 'text-warning-600' },
  stale: { dot: 'bg-error-500', text: 'Sync stale', textColor: 'text-error-600' },
};

interface SyncStatusBadgeProps {
  summary: ISyncStatusSummaryResponse | null;
  isLoading: boolean;
}

export function SyncStatusBadge({ summary, isLoading }: Readonly<SyncStatusBadgeProps>) {
  if (isLoading || !summary || typeof summary !== 'object' || Array.isArray(summary)) return null;
  if (summary.status === 'unknown') return null;

  const config = SYNC_STATUS_CONFIG[summary.status];
  if (!config) return null;
  const timeLabel = summary.lastSyncedAt ? formatRelativeTime(summary.lastSyncedAt) : null;
  const failingAreas = Array.isArray(summary.failingAreas) ? summary.failingAreas : [];
  const hasProblems = failingAreas.length > 0;

  return (
    <div className="group relative inline-flex items-center shrink-0">
      <button
        type="button"
        className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-sm hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring"
        aria-label={timeLabel ? `${config.text}, last sync ${timeLabel}` : config.text}
      >
        <span className={cn('h-2 w-2 rounded-full', config.dot)} />
        <span className={cn('font-medium', config.textColor)}>{config.text}</span>
      </button>
      <div className="invisible group-hover:visible group-focus-within:visible absolute right-0 top-full z-20 mt-1 min-w-52 rounded-md border bg-popover p-3 shadow-md">
        {timeLabel && (
          <div className="mb-1 text-xs text-muted-foreground">Last sync {timeLabel}</div>
        )}
        {hasProblems ? (
          <>
            <div className="mb-1 text-xs font-medium text-foreground">Failing areas</div>
            {failingAreas.map((area) => (
              <div key={area} className="text-xs text-muted-foreground">{'✗'} {area}</div>
            ))}
          </>
        ) : (
          <div className="text-xs text-muted-foreground">All scheduled jobs current</div>
        )}
      </div>
    </div>
  );
}
